import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MailSearch, Clock, RefreshCw, ChevronRight, Bell, Settings2, Check, X as XIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * R135 — Onboarding Watchtower tile.
 *
 * Surfaces leases currently `pending_signature` on the admin dashboard so
 * stalled DocuSign envelopes get chased before they age past 72h.
 */
function _fmtWait(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

export default function OnboardingWatchtowerCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null);
  const [cadenceEditFor, setCadenceEditFor] = useState(null);   // {propId, name, hours}
  const [savingCadence, setSavingCadence] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leases/pending-signature");
      setData(data);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/OnboardingWatchtowerCard.jsx: swallowed', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nudge = async (leaseId, unit, e) => {
    e.stopPropagation();
    setNudging(leaseId);
    try {
      const { data: r } = await api.post(`/admin/leases/${leaseId}/nudge`);
      if (r.already_nudged) {
        toast.info(`Cooldown — try again in ${r.cooldown_hours_remaining}h`);
      } else {
        toast.success(`Reminder sent (nudge #${r.nudge_count} for unit ${unit || "?"})`);
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "docusign_not_configured") {
        toast.error(`DocuSign isn't configured yet · missing ${detail.missing.join(", ")}`);
      } else {
        toast.error(detail?.message || detail || "Nudge failed");
      }
    }
    setNudging(null);
  };

  const openCadenceEditor = async (propId, propName, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/admin/properties/${propId}/nudge-cadence`);
      setCadenceEditFor({ propId, name: propName, hours: data.nudge_cooldown_hours });
    } catch (err) {
      toast.error("Failed to load current cadence");
    }
  };

  const saveCadence = async () => {
    if (!cadenceEditFor) return;
    setSavingCadence(true);
    try {
      await api.put(`/admin/properties/${cadenceEditFor.propId}/nudge-cadence`, {
        nudge_cooldown_hours: parseInt(cadenceEditFor.hours),
      });
      toast.success(`Nudge cooldown for ${cadenceEditFor.name} set to ${cadenceEditFor.hours}h`);
      setCadenceEditFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingCadence(false);
  };

  const total = data?.count ?? 0;
  const stale = data?.stale_count ?? 0;

  return (
    <Card className="p-5 bg-card border-white/10" data-testid="onboarding-watchtower-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MailSearch className="h-3.5 w-3.5 text-sky-300" />
          <div className="overline">Onboarding Watchtower</div>
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                 onClick={load} data-testid="watchtower-refresh">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {total === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center"
             data-testid="watchtower-empty">
          No leases waiting on countersign &mdash; you&apos;re all caught up.
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="text-3xl font-semibold font-display tabular-nums"
                 data-testid="watchtower-total">{total}</div>
            <div className="text-xs text-zinc-500">pending signature</div>
            {stale > 0 && (
              <Badge variant="outline"
                     className="ml-auto text-[10px] border-rose-500/40 text-rose-300 bg-rose-500/10"
                     data-testid="watchtower-stale-badge">
                {stale} stalled &gt; {data?.stale_threshold_hours ?? 72}h
              </Badge>
            )}
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {(data.items || []).slice(0, 8).map((r) => (
              <div
                key={r.lease_id}
                className="flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-white/[0.03] cursor-pointer border border-white/5"
                onClick={() => nav("/admin/leases")}
                data-testid={`watchtower-row-${r.lease_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">
                    <span className="font-medium">{r.unit || "—"}</span>
                    <span className="text-zinc-500 mx-1.5">·</span>
                    <span className="text-zinc-300">
                      {r.resident_name || r.resident_email || "unassigned"}
                    </span>
                    {r.nudge_count > 0 && (
                      <span className="ml-2 text-[10px] text-amber-300"
                             data-testid={`watchtower-nudged-${r.lease_id}`}>
                        · nudged {r.nudge_count}×
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {r.property_name || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-xs font-medium flex items-center gap-1 justify-end ${
                    r.stale ? "text-rose-300" : "text-zinc-300"}`}>
                    <Clock className="h-3 w-3" />
                    {_fmtWait(r.waiting_hours)}
                  </div>
                </div>
                <Button
                  size="sm" variant="ghost"
                  className={`h-7 w-7 p-0 shrink-0 ${
                    r.can_nudge ? "hover:bg-amber-500/10 text-amber-300"
                                 : "text-zinc-600 cursor-not-allowed"}`}
                  disabled={!r.can_nudge || nudging === r.lease_id}
                  onClick={(e) => nudge(r.lease_id, r.unit, e)}
                  data-testid={`nudge-${r.lease_id}`}
                  title={r.can_nudge ? `Resend DocuSign reminder (cooldown ${r.nudge_cooldown_hours || 12}h)`
                                       : `Cooldown — nudged within ${r.nudge_cooldown_hours || 12}h`}
                >
                  <Bell className={`h-3.5 w-3.5 ${nudging === r.lease_id ? "animate-pulse" : ""}`} />
                </Button>
                <Button
                  size="sm" variant="ghost"
                  className="h-7 w-7 p-0 shrink-0 hover:bg-white/10 text-zinc-500 hover:text-zinc-200"
                  onClick={(e) => openCadenceEditor(r.property_id, r.property_name, e)}
                  data-testid={`cadence-${r.lease_id}`}
                  title={`Tune nudge cooldown for ${r.property_name || "this property"}`}
                >
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
                <ChevronRight className="h-3 w-3 text-zinc-600 shrink-0" />
              </div>
            ))}
          </div>
          {total > 8 && (
            <div className="text-[11px] text-zinc-500 mt-2 text-center">
              +{total - 8} more · <button onClick={() => nav("/admin/leases")}
                                             className="underline hover:text-white"
                                             data-testid="watchtower-view-all">
                view all
              </button>
            </div>
          )}
        </>
      )}

      {cadenceEditFor && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2"
             data-testid="cadence-editor">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Nudge cooldown · {cadenceEditFor.name}
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number" min="1" max="168"
              value={cadenceEditFor.hours}
              onChange={(e) => setCadenceEditFor(
                { ...cadenceEditFor, hours: e.target.value })}
              className="h-8 text-sm w-24"
              data-testid="cadence-hours-input"
            />
            <span className="text-xs text-zinc-500">hours between nudges</span>
            <Button size="sm" onClick={saveCadence} disabled={savingCadence}
                     className="h-7 gap-1" data-testid="cadence-save">
              <Check className="h-3 w-3" /> {savingCadence ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost"
                     onClick={() => setCadenceEditFor(null)}
                     className="h-7 gap-1 text-zinc-500 hover:text-white"
                     data-testid="cadence-cancel">
              <XIcon className="h-3 w-3" /> Cancel
            </Button>
          </div>
          <div className="text-[10px] text-zinc-500">
            Range 1–168h · different communities tolerate different reminder cadences.
          </div>
        </div>
      )}
    </Card>
  );
}
